import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { utils } from '../../utils';
import { usePermissions } from '../../hooks';
import { SystemModule } from '../../types/permissions';
import {
  buscarEtiqueta,
  atualizarStatusEtiqueta,
  deletarPedidoCompleto,
  listarEtiquetasDoPedido,
  Etiqueta,
  EtiquetaStatus,
  STATUS_LABELS,
  STATUS_COLORS,
  STATUS_ICONS
} from '../../services/etiquetas';
import ViewLayout from '../../components/Common/ViewLayout';

const TODOS_STATUS: EtiquetaStatus[] = [
  'pendente', 'pre_carregamento', 'carregada', 'em_transito',
  'descarregada', 'conferida', 'divergente', 'com_ocorrencia'
];

const EtiquetaView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canUpdate, canDelete } = usePermissions();
  const [etiqueta, setEtiqueta] = useState<Etiqueta | null>(null);
  const [etiquetasRelacionadas, setEtiquetasRelacionadas] = useState<Etiqueta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showEtiquetas, setShowEtiquetas] = useState(true);

  useEffect(() => {
    const fetchEtiqueta = async () => {
      try {
        const response = await buscarEtiqueta(Number(id));
        if (response.success) {
          setEtiqueta(response.dados);

          // Buscar todas as etiquetas do mesmo pedido
          try {
            const relResponse = await listarEtiquetasDoPedido(Number(id));
            if (relResponse.success) {
              setEtiquetasRelacionadas(relResponse.dados);
            }
          } catch {
            // Não bloquear a visualização se falhar
          }
        }
      } catch (error: any) {
        utils.showNotification(error.message || 'Erro ao carregar pedido', { type: 'error' });
        navigate('/pedidos');
      } finally {
        setIsLoading(false);
      }
    };
    fetchEtiqueta();
  }, [id, navigate]);

  const handleStatusChange = async (novoStatus: EtiquetaStatus) => {
    if (!etiqueta || novoStatus === etiqueta.status) return;
    setIsUpdating(true);
    try {
      const response = await atualizarStatusEtiqueta(etiqueta.id, novoStatus);
      if (response.success) {
        setEtiqueta(response.dados);
        // Atualizar na lista mesclando com dados existentes (preserva campos de JOIN como palete, doca, etc.)
        setEtiquetasRelacionadas(prev =>
          prev.map(e => e.id === etiqueta.id ? { ...e, ...response.dados } : e)
        );
        utils.showNotification(`Status atualizado para "${STATUS_LABELS[novoStatus]}"`, { type: 'success' });
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao atualizar status', { type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!etiqueta) return;
    if (!window.confirm(`Tem certeza que deseja excluir o pedido ${etiqueta.numero_pedido || etiqueta.numero_nfe || etiqueta.id} e todas as suas etiquetas?`)) return;
    try {
      // Coletar IDs de todas as etiquetas relacionadas ao pedido
      const ids = etiquetasRelacionadas.length > 0
        ? etiquetasRelacionadas.map(e => e.id)
        : [etiqueta.id];
      const response = await deletarPedidoCompleto(ids);
      if (response.success) {
        utils.showNotification('Pedido excluído com sucesso', { type: 'success' });
        navigate('/pedidos');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao excluir pedido', { type: 'error' });
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (value?: number): string => {
    if (!value && value !== 0) return '-';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatCNPJ = (cnpj?: string): string => {
    if (!cnpj) return '-';
    if (cnpj.length === 14) {
      return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    if (cnpj.length === 11) {
      return cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cnpj;
  };

  const etiquetasBipadas = etiquetasRelacionadas.filter(e => e.status !== 'pendente');
  const totalEtiquetas = etiquetasRelacionadas.length;
  const totalBipadas = etiquetasBipadas.length;

  // Calcular status do pedido com base em todas as etiquetas relacionadas
  // Status de erro/problema têm prioridade, senão usa o status mais avançado
  const calcularStatusPedido = (): EtiquetaStatus => {
    const todas = etiquetasRelacionadas.length > 0 ? etiquetasRelacionadas : (etiqueta ? [etiqueta] : []);
    if (todas.length === 0) return etiqueta?.status as EtiquetaStatus || 'pendente';

    // Status de problema têm prioridade
    if (todas.some(e => e.status === 'divergente')) return 'divergente';
    if (todas.some(e => e.status === 'com_ocorrencia')) return 'com_ocorrencia';

    // Se TODAS têm o mesmo status, usa ele
    const todosIguais = todas.every(e => e.status === todas[0].status);
    if (todosIguais) return todas[0].status as EtiquetaStatus;

    // Para status mistos, usa o mais avançado na progressão
    const progressOrder: EtiquetaStatus[] = ['pendente', 'pre_carregamento', 'carregada', 'em_transito', 'descarregada', 'conferida'];
    let maxIdx = 0;
    for (const e of todas) {
      const idx = progressOrder.indexOf(e.status as EtiquetaStatus);
      if (idx > maxIdx) maxIdx = idx;
    }
    return progressOrder[maxIdx];
  };

  const statusPedido = calcularStatusPedido();

  return (
    <ViewLayout
      title="Detalhes do Pedido"
      subtitle={etiqueta ? `Pedido ${etiqueta.numero_pedido || '-'} | NF-e ${etiqueta.numero_nfe || '-'} | ${etiqueta.cliente_nome || ''}` : ''}
      isLoading={isLoading}
      onBack={() => navigate('/pedidos')}
      editUrl={canUpdate(SystemModule.CONFERENCIA_CARGA) && etiqueta ? `/pedidos/${etiqueta.id}/editar` : undefined}
      onDelete={canDelete(SystemModule.CONFERENCIA_CARGA) ? handleDelete : undefined}
      deleteButtonText="Excluir Pedido"
    >
      {etiqueta && (
        <>
          {/* Status atual + Timeline */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Status do Pedido
            </h3>

            {/* Badge do status atual */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '8px 16px', borderRadius: '20px', fontSize: '15px', fontWeight: 600,
                color: '#fff', backgroundColor: STATUS_COLORS[statusPedido] || '#6b7280'
              }}>
                <i className={STATUS_ICONS[statusPedido] || 'fas fa-circle'}></i>
                {STATUS_LABELS[statusPedido] || statusPedido}
              </span>

              {/* Alterar status */}
              <select
                className="form-control"
                style={{ width: '220px' }}
                value={statusPedido}
                onChange={(e) => handleStatusChange(e.target.value as EtiquetaStatus)}
                disabled={isUpdating}
              >
                {TODOS_STATUS.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
              {isUpdating && <div className="loading-spinner-small"></div>}
            </div>

            {/* Timeline de status - combina histórico de todas as etiquetas do pedido */}
            {(() => {
              const todas = etiquetasRelacionadas.length > 0 ? etiquetasRelacionadas : [etiqueta];
              const todasEntradas: { status: string; data: string; descricao?: string; volume?: number }[] = [];

              for (const etq of todas) {
                if (etq.status_detalhes && etq.status_detalhes.length > 0) {
                  for (const d of etq.status_detalhes) {
                    todasEntradas.push({
                      status: d.status,
                      data: d.data,
                      descricao: d.descricao,
                      volume: etq.volume_atual
                    });
                  }
                }
              }

              // Agrupar por status, mantendo a data mais antiga (primeira ocorrência)
              const porStatus = new Map<string, { status: string; data: string; descricao?: string; volumes: number[] }>();
              for (const e of todasEntradas) {
                const existing = porStatus.get(e.status);
                if (!existing || new Date(e.data) < new Date(existing.data)) {
                  porStatus.set(e.status, {
                    status: e.status,
                    data: e.data,
                    descricao: e.descricao,
                    volumes: existing ? [...existing.volumes, ...(e.volume ? [e.volume] : [])] : (e.volume ? [e.volume] : [])
                  });
                } else if (existing && e.volume) {
                  existing.volumes.push(e.volume);
                }
              }

              const timeline = Array.from(porStatus.values()).sort(
                (a, b) => new Date(a.data).getTime() - new Date(b.data).getTime()
              );

              return timeline.length > 0 ? (
                <div style={{ padding: '0 0 0 20px', borderLeft: '3px solid #e5e7eb' }}>
                  {timeline.map((detalhe, idx) => {
                    const sKey = detalhe.status as EtiquetaStatus;
                    const isLast = idx === timeline.length - 1;
                    const dotColor = isLast ? '#04C64F' : (STATUS_COLORS[sKey] || '#d1d5db');
                    return (
                      <div key={idx} style={{ marginBottom: '12px', position: 'relative' }}>
                        <div style={{
                          position: 'absolute', left: '-28px', top: '2px',
                          width: '12px', height: '12px', borderRadius: '50%',
                          backgroundColor: dotColor,
                          border: '2px solid #fff'
                        }}></div>
                        <div style={{ fontSize: '13px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                            color: '#fff', backgroundColor: STATUS_COLORS[sKey] || '#6b7280'
                          }}>
                            <i className={STATUS_ICONS[sKey] || 'fas fa-circle'} style={{ fontSize: '8px' }}></i>
                            {STATUS_LABELS[sKey] || detalhe.status}
                          </span>
                          <span style={{ color: '#9ca3af', marginLeft: '8px', fontSize: '12px' }}>
                            {formatDate(detalhe.data)}
                          </span>
                          {detalhe.descricao && (
                            <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '12px' }}>{detalhe.descricao}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null;
            })()}
          </div>

          {/* Itens do Pedido (produtos da NF-e) */}
          {(() => {
            const itensComProduto = etiquetasRelacionadas.filter(e =>
              e.dados_etiqueta && (e.dados_etiqueta.codigo_produto || e.dados_etiqueta.descricao_produto || e.dados_etiqueta.quantidade || e.dados_etiqueta.valor_total_produto)
            );
            return itensComProduto.length > 0 ? (
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
                  <i className="fas fa-box-open" style={{ marginRight: '8px' }}></i>
                  Itens do Pedido
                  <span style={{ marginLeft: '12px', fontSize: '13px', fontWeight: 400, color: '#6b7280' }}>
                    {itensComProduto.length} {itensComProduto.length === 1 ? 'item' : 'itens'}
                  </span>
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>SKU</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Descrição</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>NCM</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px' }}>Qtd</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px' }}>Valor Unit.</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px' }}>Valor Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensComProduto.map((etq) => {
                        const d = etq.dados_etiqueta!;
                        return (
                          <tr key={`item-${etq.id}`}>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 500, color: '#1e40af' }}>
                              {d.codigo_produto || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', color: '#111827' }}>
                              {d.descricao_produto || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                              {d.ncm || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500 }}>
                              {d.quantidade || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                              {d.valor_unitario ? formatCurrency(parseFloat(d.valor_unitario)) : '-'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>
                              {d.valor_total_produto ? formatCurrency(parseFloat(d.valor_total_produto)) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null;
          })()}

          {/* Etiquetas Bipadas */}
          {etiquetasRelacionadas.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <h3
                style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                onClick={() => setShowEtiquetas(!showEtiquetas)}
              >
                <span>
                  <i className="fas fa-barcode" style={{ marginRight: '8px' }}></i>
                  Etiquetas do Pedido
                  <span style={{
                    marginLeft: '12px', fontSize: '13px', fontWeight: 400, color: '#6b7280'
                  }}>
                    {totalBipadas} bipada{totalBipadas !== 1 ? 's' : ''} de {totalEtiquetas} etiqueta{totalEtiquetas !== 1 ? 's' : ''}
                  </span>
                </span>
                <i className={`fas fa-chevron-${showEtiquetas ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '14px' }}></i>
              </h3>

              {showEtiquetas && (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Volume</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Código de Barras</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px', textAlign: 'center' }}>Palete</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Doca</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Placa</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Conferente</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Filial</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Status</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Bipada</th>
                        <th style={{ padding: '10px 12px', fontSize: '12px' }}>Última Atualização</th>
                      </tr>
                    </thead>
                    <tbody>
                      {etiquetasRelacionadas.map((etq) => {
                        const statusKey = etq.status as EtiquetaStatus;
                        const foiBipada = etq.status !== 'pendente';
                        return (
                          <tr key={etq.id} style={{
                            backgroundColor: etq.id === etiqueta.id ? '#f0f9ff' : undefined
                          }}>
                            <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                              {etq.volume_atual && etq.total_volumes_pedido
                                ? `${etq.volume_atual} / ${etq.total_volumes_pedido}`
                                : etq.volume_atual || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: foiBipada ? '#111827' : '#9ca3af' }}>
                              {etq.codigo_barras || 'Não bipada'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600, color: '#0891b2' }}>
                              {etq.numero_palete ? `#${etq.numero_palete}` : '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#374151', fontWeight: 500 }}>
                              {etq.doca || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: '#7c3aed', whiteSpace: 'nowrap' }}>
                              {etq.placa_veiculo || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#374151' }}>
                              {etq.conferente_nome || '-'}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#374151' }}>
                              {etq.filial_nome || '-'}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '3px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#fff',
                                backgroundColor: STATUS_COLORS[statusKey] || '#6b7280',
                                whiteSpace: 'nowrap'
                              }}>
                                <i className={STATUS_ICONS[statusKey] || 'fas fa-circle'} style={{ fontSize: '9px' }}></i>
                                {STATUS_LABELS[statusKey] || etq.status}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              {foiBipada ? (
                                <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '16px' }} title="Bipada"></i>
                              ) : (
                                <i className="fas fa-times-circle" style={{ color: '#ef4444', fontSize: '16px' }} title="Não bipada"></i>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                              {formatDate(etq.atualizado_em)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Dados da NF-e */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              <i className="fas fa-file-invoice" style={{ marginRight: '8px' }}></i>
              Dados da Nota Fiscal
            </h3>
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <InfoItem label="Número NF-e" value={etiqueta.numero_nfe || '-'} />
              <InfoItem label="Série" value={etiqueta.serie_nfe || '-'} />
              <InfoItem label="Data Emissão" value={formatDate(etiqueta.data_emissao)} />
              <InfoItem label="Valor NF-e" value={formatCurrency(etiqueta.valor_nfe)} />
              <InfoItem label="Peso Total" value={etiqueta.peso_total ? `${etiqueta.peso_total} kg` : '-'} />
              <InfoItem label="Volumes" value={etiqueta.volumes?.toString() || '-'} />
              <InfoItem label="Cubagem" value={etiqueta.cubagem ? `${etiqueta.cubagem} m³` : '-'} />
              <InfoItem label="Chave NF-e" value={etiqueta.chave_nfe || '-'} mono />
            </div>
          </div>

          {/* Emitente e Destinatário */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              <i className="fas fa-exchange-alt" style={{ marginRight: '8px' }}></i>
              Emitente / Destinatário
            </h3>
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
              <InfoItem label="Emitente" value={etiqueta.emitente_nome || '-'} />
              <InfoItem label="CNPJ Emitente" value={formatCNPJ(etiqueta.emitente_cnpj)} />
              <InfoItem label="Destinatário" value={etiqueta.destinatario_nome || '-'} />
              <InfoItem label="CNPJ/CPF Destinatário" value={formatCNPJ(etiqueta.destinatario_cnpj)} />
            </div>

            {/* Endereço de Destino */}
            {(etiqueta.dest_logradouro || etiqueta.dest_municipio || etiqueta.dest_uf) && (
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                  <i className="fas fa-map-marker-alt" style={{ marginRight: '6px' }}></i>
                  Endereço de Destino
                </h4>
                <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                  <InfoItem label="Logradouro" value={etiqueta.dest_logradouro || '-'} />
                  <InfoItem label="Número" value={etiqueta.dest_numero || '-'} />
                  <InfoItem label="Bairro" value={etiqueta.dest_bairro || '-'} />
                  <InfoItem label="Município" value={etiqueta.dest_municipio || '-'} />
                  <InfoItem label="UF" value={etiqueta.dest_uf || '-'} />
                  <InfoItem label="CEP" value={etiqueta.dest_cep ? etiqueta.dest_cep.replace(/(\d{5})(\d{3})/, '$1-$2') : '-'} />
                </div>
              </div>
            )}
          </div>

          {/* Dados do Pedido */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              <i className="fas fa-file-invoice" style={{ marginRight: '8px' }}></i>
              Dados do Pedido
            </h3>

            {/* Origem do pedido */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '6px 14px', borderRadius: '8px', marginBottom: '16px',
              fontSize: '13px', fontWeight: 500,
              backgroundColor: etiqueta.importacao_id ? '#eff6ff' : '#f0fdf4',
              color: etiqueta.importacao_id ? '#1d4ed8' : '#15803d',
              border: `1px solid ${etiqueta.importacao_id ? '#bfdbfe' : '#bbf7d0'}`
            }}>
              <i className={etiqueta.importacao_id ? 'fas fa-file-import' : 'fas fa-keyboard'} style={{ fontSize: '14px' }}></i>
              {etiqueta.importacao_id ? 'Importado via arquivo (XML/EDI)' : 'Incluído manualmente'}
            </div>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <InfoItem label="Número do Pedido" value={etiqueta.numero_pedido || '-'} mono />
              <InfoItem label="Cliente" value={etiqueta.cliente_nome || '-'} />
              <InfoItem label="Volume" value={
                etiqueta.volume_atual && etiqueta.total_volumes_pedido
                  ? `${etiqueta.volume_atual} de ${etiqueta.total_volumes_pedido}`
                  : '-'
              } />
              <InfoItem label="Código de Barras" value={etiqueta.codigo_barras || '-'} mono />
            </div>

            {/* Dados extras (exclui campos já exibidos na tabela de itens) */}
            {(() => {
              const camposExibidosNaTabela = ['codigo_produto', 'descricao_produto', 'quantidade', 'valor_unitario', 'valor_total_produto', 'ncm'];
              const dadosExtras = etiqueta.dados_etiqueta
                ? Object.entries(etiqueta.dados_etiqueta).filter(([key]) => !camposExibidosNaTabela.includes(key))
                : [];
              return dadosExtras.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                  <h4 style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Dados Extras</h4>
                  <div style={{
                    background: '#f9fafb', borderRadius: '8px', padding: '12px',
                    fontSize: '13px', fontFamily: 'monospace'
                  }}>
                    {dadosExtras.map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '4px' }}>
                        <span style={{ color: '#6b7280' }}>{key}:</span>{' '}
                        <span style={{ color: '#111827', fontWeight: 500 }}>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          {/* Metadados */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '16px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
              Informações do Sistema
            </h3>
            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <InfoItem label="ID" value={`#${etiqueta.id}`} />
              <InfoItem label="Importação ID" value={etiqueta.importacao_id ? `#${etiqueta.importacao_id}` : '-'} />
              <InfoItem label="Conferência ID" value={etiqueta.conferencia_id ? `#${etiqueta.conferencia_id}` : '-'} />
              <InfoItem label="Data de Criação" value={formatDate(etiqueta.criado_em)} />
              <InfoItem label="Última Atualização" value={formatDate(etiqueta.atualizado_em)} />
            </div>
          </div>
        </>
      )}
    </ViewLayout>
  );
};

// Componente auxiliar para exibir campos de informação
const InfoItem: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="form-group">
    <label className="form-label" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{label}</label>
    <div style={{
      fontSize: '14px', fontWeight: 500, color: '#111827',
      fontFamily: mono ? 'monospace' : 'inherit',
      wordBreak: 'break-all'
    }}>
      {value}
    </div>
  </div>
);

export default EtiquetaView;
